/**
 * AuthContext.js
 *
 * FIX: login() now auto-sets activeTenant from user.tenant_id so every
 *      component can rely on activeTenant instead of doing
 *      (activeTenant || user?.tenant_id) as a workaround.
 * FIX: Listens for the "auth:force-logout" event dispatched by axiosInstance
 *      when a token refresh fails, so the UI reacts cleanly.
 */
import React, {createContext,useContext,useState,useEffect,useCallback,} from "react";
import { useNavigate } from "react-router-dom";
import {getUser,getActiveTenant,setUser,setActiveTenant,saveSession,clear,} from "../api/tokenStorage";
import { authApi } from "../api/services";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user,          setUserState]          = useState(null);
  const [activeTenant,  setActiveTenantState]  = useState(null);
  const [isLoading,     setIsLoading]          = useState(true);

  const navigate = useNavigate();

  // ── Hydrate from localStorage on mount ──────────────────────────────────
  useEffect(() => {
    const storedUser   = getUser();
    const storedTenant = getActiveTenant();

    if (storedUser) {
      setUserState(storedUser);
      // FIX: if no stored tenant yet (e.g. first login before fix), derive from user
      if (storedTenant) {
        setActiveTenantState(storedTenant);
      } else if (storedUser.tenant_id) {
        const derived = { id: storedUser.tenant_id, name: storedUser.org_name };
        setActiveTenantState(derived);
        setActiveTenant(derived);
      }
    }
    setIsLoading(false);
  }, []);

  // ── Listen for force-logout events from axiosInstance ───────────────────
  useEffect(() => {
    const handler = () => {
      setUserState(null);
      setActiveTenantState(null);
      navigate("/", { replace: true });
    };
    window.addEventListener("auth:force-logout", handler);
    return () => window.removeEventListener("auth:force-logout", handler);
  }, [navigate]);

  // ── login ────────────────────────────────────────────────────────────────
  // const login = useCallback(async (email, password) => {
  //   const res  = await authApi.login({ email, password });
  //   const body = res.data?.data || res.data;  // handle both enveloped + bare

  //   const { access_token, refresh_token, user: userData } = body;

  //   saveSession({ access_token, refresh_token, user: userData });
  //   setUserState(userData);

  //   // FIX: derive and store activeTenant immediately so every component
  //   // can use activeTenant without the (activeTenant || user?.tenant_id) workaround.
  //   if (userData?.tenant_id) {
  //     const tenant = { id: userData.tenant_id, name: userData.org_name };
  //     setActiveTenant(tenant);
  //     setActiveTenantState(tenant);
  //   }

  //   return userData;
  // }, []);
const login = useCallback((data) => {
  saveSession({
    access_token:  data.access_token,
    refresh_token: data.refresh_token,
    user:          data.user,
  });
  setUserState(data.user);

  if (data.user?.tenant_id) {
    const tenant = { id: data.user.tenant_id, name: data.user.org_name };
    setActiveTenant(tenant);
    setActiveTenantState(tenant);
  }
}, []);
  // ── logout ───────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore — we clear local state regardless
    }
    clear();
    setUserState(null);
    setActiveTenantState(null);
    navigate("/", { replace: true });
  }, [navigate]);

  // ── selectTenant (super_admin only) ─────────────────────────────────────
  const selectTenant = useCallback((tenant) => {
    setActiveTenant(tenant);
    setActiveTenantState(tenant);
  }, []);

  // ── hasRole ──────────────────────────────────────────────────────────────
  const hasRole = useCallback(
    (...roles) => roles.includes(user?.role),
    [user]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        activeTenant,
        isLoading,
        login,
        logout,
        selectTenant,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};